/**
 * Manages operations related to image fields using MT9's AssetUploader.
 * @class
 */
import { removeHost } from './removeHost.js';
import { getDataApiUrl } from './getDataApiUrl.js';

export class ImageField {
    /**
     * ImageField クラスを生成するためのコンストラクタ。
     *
     * @class
     * @param {string} id - 画像フィールドのID。この引数は必須です。
     * @param {string} handle - フィールドハンドル
     * @param {object} options - オプション
     * @param {number} options.thumbnailWidth - サムネイルの幅
     * @param {number} options.thumbnailHeight - サムネイルの高さ
     * @param {string} options.uploadDirectory - アップロードディレクトリ
     * @param {object} storage - Svelteのストレージオブジェクト
     * @param {string} storageKey - ストレージキー
     * @param {Function} triggerInputChange - input変更イベントを発火する関数
     * @throws {Error} id パラメータが指定されていない場合、エラーをスローします。
     */
    constructor(id, handle = '', options = {}, storage = null, storageKey = '', triggerInputChange = null) {
        if (!id) {
            throw new Error('The "id" parameter is required as the first argument.');
        }
        this.id = id;
        this.handle = handle;
        this.thumbnailWidth = options.thumbnailWidth || 120;
        this.thumbnailHeight = options.thumbnailHeight || 120;
        this.uploadDirectory = options.uploadDirectory || '';
        this.storage = storage;
        this.storageKey = storageKey;
        this.triggerInputChange = triggerInputChange;
    }

    //----------------------------------------
    // 静的プロパティ
    //----------------------------------------

    /** @static @property {boolean} initialized - 初期化済みフラグ */
    static initialized = false;

    /**
     * open() 呼び出し時に指定された uploadDirectory を一時保持する。
     * MT.export('uploadAsset') のラッパーが参照し、アップロード時の
     * destination を差し替えるために使用する。
     * @static @property {string}
     */
    static _pendingUploadDirectory = '';

    /** @static @property {MutationObserver|null} */
    static _destinationObserver = null;

    //----------------------------------------
    // 静的メソッド
    //----------------------------------------

    /**
     * ImageField クラスの初期化メソッド。
     * AssetUploaderのinsertメソッドを上書きしてアセット選択イベントを発火します。
     *
     * @static
     */
    static init() {
        if (ImageField.initialized) {
            return;
        }

        // MT.AssetUploaderが利用可能かチェック
        // 注: MT9のスクリプトがまだ読み込まれていない可能性があるため、
        // 利用不可の場合は何もせずにリターンする（警告は出さない）
        if (!window.MT?.AssetUploader) {
            return;
        }

        // AssetUploaderのopenメソッドを上書き
        const originalOpen = window.MT.AssetUploader.open;

        window.MT.AssetUploader.open = (opts) => {
            // uploadDirectory が指定されていれば保持（未指定なら空にリセット）
            ImageField._pendingUploadDirectory = opts.uploadDirectory || '';

            const originalInsert = opts.insert;

            // insertメソッドをラップしてアセット選択時にイベントを発火
            opts.insert = async (data) => {
                // 元のinsert処理を実行
                if (originalInsert) {
                    await originalInsert(data);
                }

                // アセット情報を取得してカスタムイベントを発火
                const assetInfo = data.map((item) => ({
                    id: item.asset.id,
                    url: item.asset.url,
                    label: item.asset.label,
                    width: item.asset.width,
                    height: item.asset.height,
                    class: item.asset.class,
                    mimeType: item.asset.mimeType
                }));

                window.dispatchEvent(
                    new CustomEvent('mtapp:assetSelected', {
                        detail: { assets: assetInfo }
                    })
                );
            };

            const result = originalOpen(opts);

            // uploadDirectory 指定時はオプション画面の UI を監視・反映
            ImageField._destinationObserver?.disconnect();
            ImageField._destinationObserver = null;
            if (ImageField._pendingUploadDirectory) {
                ImageField._setupDestinationObserver();
            }

            return result;
        };

        // uploadAsset関数を上書き
        if (window.MT?.export) {
            window.MT.export('uploadAsset', ({ original }) => async (args) => {
                // uploadDirectory が指定されている場合、destination を差し替え
                if (ImageField._pendingUploadDirectory) {
                    args = {
                        ...args,
                        options: {
                            ...args.options,
                            destination: ImageField._pendingUploadDirectory,
                            extraPath: ''
                        }
                    };
                }

                const result = await original(args);

                // アップロード完了後にアセットIDを取得してイベントを発火
                if (result && result.ok) {
                    try {
                        const cloned = result.clone();
                        const responseData = await cloned.json();
                        if (responseData.result && responseData.result.asset) {
                            window.dispatchEvent(
                                new CustomEvent('mtapp:assetUploaded', {
                                    detail: {
                                        assetId: responseData.result.asset.id,
                                        asset: responseData.result.asset
                                    }
                                })
                            );
                        }
                    } catch {
                        // Response が clone 不可の場合は無視
                    }
                }

                return result;
            });
        }

        ImageField.initialized = true;
    }

    /**
     * オプション画面に表示される「アップロード先」の Destination コンポーネントを
     * 監視し、表示された時点で uploadDirectory の値を反映する。
     * @static
     */
    static _setupDestinationObserver() {
        const modal = document.getElementById('mt-asset-uploader-modal');
        if (!modal) {
            return;
        }

        ImageField._destinationObserver = new MutationObserver(() => {
            if (!document.getElementById('mt-asset-uploader-modal')) {
                ImageField._destinationObserver?.disconnect();
                ImageField._destinationObserver = null;
                return;
            }

            /** @type {HTMLElement|null} */
            const container = modal.querySelector('mt-asset-uploader-upload-destination');
            if (!container || container.dataset.mtappOverridden) {
                return;
            }

            container.dataset.mtappOverridden = 'true';
            ImageField._applyDestinationOverride(container);
        });

        ImageField._destinationObserver.observe(modal, {
            childList: true,
            subtree: true
        });
    }

    /**
     * Destination コンポーネントの内容を uploadDirectory の値で上書きし readonly にする。
     * select が表示されている場合はカスタムモードに切り替えてから値を設定する。
     * @static
     * @param {HTMLElement} container - mt-asset-uploader-upload-destination 要素
     */
    static _applyDestinationOverride(container) {
        /** @type {HTMLSelectElement|null} */
        const select = container.querySelector('select[aria-describedby="uploadDestination"]');

        if (select) {
            select.value = '';
            select.dispatchEvent(new Event('change', { bubbles: true }));

            requestAnimationFrame(() => {
                ImageField._setDestinationInput(container);
            });
        } else {
            ImageField._setDestinationInput(container);
        }
    }

    /**
     * カスタムモードの入力欄に uploadDirectory の値を設定して readonly にする。
     * @static
     * @param {HTMLElement} container
     */
    static _setDestinationInput(container) {
        /** @type {HTMLInputElement|null} */
        const input = container.querySelector('input[type="text"]');
        if (!input) {
            return;
        }

        input.value = ImageField._pendingUploadDirectory;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.readOnly = true;
        input.style.backgroundColor = '#e9ecef';
    }

    /**
     * 指定された要素の値を設定し、'input' と 'blur' イベントをディスパッチします。
     *
     * @static
     * @param {HTMLInputElement} el - 値を設定する対象のDOM要素。
     * @param {string|number} value - 要素に設定する値。
     */
    static setElementValue(el, value) {
        if (el) {
            el.value = value;
            el.dispatchEvent(new Event('input'));
            el.dispatchEvent(new Event('blur'));
        }
    }

    /**
     * アセット情報を取得する
     *
     * @static
     * @param {string|number} assetId - アセットID
     * @returns {Promise<Object>} アセット情報
     */
    static async getAssetInfo(assetId) {
        try {
            const apiUrl = getDataApiUrl();
            const url = `${apiUrl}/assetinfo/${assetId}`;
            const response = await fetch(url, {
                method: 'GET'
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (data.error) {
                throw new Error(data.error.message);
            }

            return data;
        } catch (error) {
            console.error('Error fetching asset info:', error);
            throw error;
        }
    }

    /**
     * サムネイルURLを取得する
     *
     * @static
     * @param {string|number} siteId - サイトID
     * @param {string|number} assetId - アセットID
     * @param {number} width - サムネイルの幅
     * @param {number} height - サムネイルの高さ
     * @returns {Promise<string>} サムネイルURL
     */
    static async getThumbnailUrl(siteId, assetId, width, height) {
        try {
            const apiUrl = getDataApiUrl();
            const params = new URLSearchParams({
                width: String(width),
                height: String(height)
            });
            const url = `${apiUrl}/sites/${siteId}/assets/${assetId}/thumbnail?${params}`;
            const response = await fetch(url, {
                method: 'GET'
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (data.error) {
                throw new Error(data.error.message);
            }

            return data.url;
        } catch (error) {
            console.error('Error fetching thumbnail:', error);
            throw error;
        }
    }

    //----------------------------------------
    // インスタンスプロパティ
    //----------------------------------------

    id = '';
    handle = '';
    thumbnailWidth = 120;
    thumbnailHeight = 120;
    uploadDirectory = '';
    storage = null;
    storageKey = '';
    triggerInputChange = null;

    //----------------------------------------
    // インスタンスメソッド
    //----------------------------------------

    /**
     * 画像を選択するためのメソッド。
     * MT9のAssetUploaderダイアログを開きます。
     * @method
     * @this ImageField
     */
    select() {
        // MT.AssetUploaderが利用可能かチェック
        if (!window.MT?.AssetUploader) {
            alert('AssetUploader is not available. This feature requires MT9 or later.');
            return false;
        }

        ImageField.init();

        const targetIdElement = document.getElementById(this.id);
        const targetUrlElement = document.getElementById(this.id + '-url');
        const targetThumbElement = document.getElementById(this.id + '-thumb');
        const updateItemIdButton = targetIdElement?.previousElementSibling;

        // AssetUploaderダイアログを開く
        window.MT.AssetUploader.open({
            // 画像のみフィルタ
            filter: {
                class: 'image'
            },
            // アップロード先ディレクトリ
            ...(this.uploadDirectory && { uploadDirectory: this.uploadDirectory }),
            // アセット選択時のコールバック
            insert: async (data) => {
                if (data.length === 0) {
                    return;
                }

                // 最初のアセットを使用（単一選択を想定）
                const item = data[0];

                // アセット情報を正規化
                const asset = {
                    id: item.assetId,
                    url: item.assetUrl,
                    class: 'image',
                    width: item.assetWidth,
                    height: item.assetHeight
                };

                // サムネイルURLを取得（段階的フォールバック）
                try {
                    // blog_idをsiteIdとして直接サムネイルを取得（getAssetInfoを経由しない）
                    const siteId = window.mtappVars.blog_id;
                    const thumbnailUrl = await ImageField.getThumbnailUrl(
                        siteId,
                        asset.id,
                        this.thumbnailWidth,
                        this.thumbnailHeight
                    );
                    asset.thumbnail = thumbnailUrl;
                } catch {
                    // Data APIが失敗した場合、サーバーレスポンスのサムネイルURLを使用
                    if (item.assetThumbnailUrl && item.assetThumbnailUrl !== item.assetUrl) {
                        asset.thumbnail = item.assetThumbnailUrl;
                    } else {
                        asset.thumbnail = asset.url;
                    }
                }

                // Svelteのストレージを更新
                if (this.storage && this.storageKey) {
                    this.storage[this.storageKey].id = asset.id;
                    this.storage[this.storageKey].url = removeHost(asset.url);

                    // API経由で取得したサムネイルURLを使用
                    if (asset.thumbnail) {
                        this.storage[this.storageKey].thumbnail = removeHost(asset.thumbnail);
                    }

                    // 変更イベントを発火
                    if (this.triggerInputChange) {
                        this.triggerInputChange();
                    }
                }

                // hiddenフィールドも更新（互換性のため）
                if (targetIdElement) {
                    ImageField.setElementValue(targetIdElement, asset.id);
                }

                if (targetUrlElement && asset.url) {
                    ImageField.setElementValue(targetUrlElement, removeHost(asset.url));
                }

                if (targetThumbElement && asset.thumbnail) {
                    ImageField.setElementValue(targetThumbElement, removeHost(asset.thumbnail));
                }

                // MultiFieldProの更新ボタンをクリック
                if (updateItemIdButton instanceof HTMLElement) {
                    updateItemIdButton.click();
                }
            }
        });

        return false;
    }

    /**
     * 画像を削除するメソッド
     */
    remove() {
        const idField = document.getElementById(this.id);
        if (idField) {
            ImageField.setElementValue(idField, '');
        }

        const thumbField = document.getElementById(this.id + '-thumb');
        if (thumbField) {
            ImageField.setElementValue(thumbField, '');
        }

        const urlField = document.getElementById(this.id + '-url');
        if (urlField) {
            ImageField.setElementValue(urlField, '');
        }
    }
}
